import { useState, Fragment } from 'react';
import { B } from '../constants/brand';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { fmt } from '../utils/format';
import { Btn, Spinner, ErrorMsg, Card, SectionHeader, useToast, Icon } from '../components/ui';
import { InlineSearchPicker, useDealPickerItems, useTaskPickerItems } from '../components/tasks/TaskComponents';

// ── Email Inbox ──────────────────────────────────────────────────────────────
// Forwarded / CC'd emails captured by the Outlook email-capture pipeline land
// here as "Pending", GROUPED BY THREAD (Graph conversationId). An admin picks
// the deal, deal detail, or task each thread belongs to and attaches it, which
// creates ONE rolling note (Claude summary + each email as PDF + attachments)
// and starts tracking: later replies in the conversation auto-file to the same
// place. Dismiss removes captures that shouldn't become a note.

const ENTITY_LABEL = { deal: 'Deal', loan: 'Loan', investment: 'Investment', realEstate: 'Real Estate' };

// Group flat pending captures into threads by conversationId. Items without a
// conversationId (legacy / add-in captures) each become their own group.
function groupThreads(items) {
  const groups = new Map();
  for (const it of (items || [])) {
    const key = it.conversationId || `solo:${it.inboxID}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(it);
  }
  return [...groups.values()].map(msgs => {
    const ordered = [...msgs].sort((a, b) =>
      new Date(a.receivedUTC || a.createdUTC || 0) - new Date(b.receivedUTC || b.createdUTC || 0));
    const newest = ordered[ordered.length - 1];
    // Union of participants across the thread (by address), for display.
    const seen = new Map();
    for (const m of ordered) {
      const add = (name, address) => { const a = (address || name || '').toLowerCase(); if (a && !seen.has(a)) seen.set(a, name || address); };
      add(m.fromName, m.fromEmail);
      for (const r of (m.toRecipients || [])) add(r.name, r.address);
      for (const r of (m.ccRecipients || [])) add(r.name, r.address);
    }
    // First non-empty suggestion set in the thread.
    const suggestions = (ordered.find(m => (m.suggestions || []).length)?.suggestions) || [];
    return {
      key: newest.conversationId || newest.inboxID,
      items: ordered,
      newest,
      subject: newest.subject || '(no subject)',
      participants: [...seen.values()],
      suggestions,
      attachmentCount: ordered.reduce((n, m) => n + (m.attachmentCount || 0), 0),
    };
  }).sort((a, b) => new Date(b.newest.receivedUTC || b.newest.createdUTC || 0) - new Date(a.newest.receivedUTC || a.newest.createdUTC || 0));
}

const ThreadCard = ({ group, dealItems, taskItems, onAssigned }) => {
  const { toast }                 = useToast();
  const [targetType, setTargetType] = useState('deal'); // 'deal' | 'task'
  const [dealCombo, setDealCombo]   = useState('');
  const [taskCombo, setTaskCombo]   = useState('');
  const [busy, setBusy]           = useState(false);
  const [showAll, setShowAll]     = useState(false);

  const { items, newest, subject, participants, suggestions } = group;
  const isThread = items.length > 1;

  // Attaching any message assigns the whole thread — the backend folds every
  // pending capture in the conversation into one rolling note.
  const assignTo = async ({ dealId, dealDetailId, taskId }) => {
    setBusy(true);
    try {
      const r = await api.post(`email-inbox/${newest.inboxID}/assign`, {
        dealId: dealId || null, dealDetailId: dealDetailId || null, taskId: taskId || null,
      });
      // Attachments land on the deal's Documents tab too, not just on the note —
      // say so, because that is where people go looking for the file.
      const docs = r?.documentCount || 0;
      const filed = docs ? ` — ${docs} ${docs === 1 ? 'file' : 'files'} filed to Documents` : '';
      // Filing an email notifies the deal's Harvus leads. Reported either way,
      // INCLUDING zero: a filing that told nobody looks exactly like one that
      // told everybody, which is how a lead can stop hearing about a deal for a
      // week before anyone notices. Zero is often correct (you are the only
      // lead), so it is stated plainly rather than dressed as a failure.
      const n = r?.notified || 0;
      const told = n
        ? ` · ${n} ${n === 1 ? 'lead' : 'leads'} notified`
        : ' · nobody notified';
      toast.success((isThread
        ? 'Thread attached — new replies will track automatically'
        : 'Email attached as a note') + filed + told);
      onAssigned();
    } catch (e) { toast.error(e.message || 'Assign failed'); }
    finally { setBusy(false); }
  };

  const assign = () => {
    if (targetType === 'task') {
      const sel = (taskItems || []).find(i => i.value === taskCombo);
      if (!sel) { toast.error('Pick a task first'); return; }
      assignTo({ taskId: sel.taskId });
    } else {
      const sel = (dealItems || []).find(i => i.value === dealCombo);
      if (!sel) { toast.error('Pick a deal first'); return; }
      assignTo({ dealId: sel.dealID, dealDetailId: sel.dealDetailID || null });
    }
  };

  const dismiss = async () => {
    const msg = isThread
      ? `Dismiss all ${items.length} captured emails in this thread? They will not become a note.`
      : 'Dismiss this captured email? It will not become a note.';
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      for (const it of items) await api.post(`email-inbox/${it.inboxID}/dismiss`, {});
      toast.success(isThread ? 'Thread dismissed' : 'Capture dismissed');
      onAssigned();
    } catch (e) { toast.error(e.message || 'Dismiss failed'); }
    finally { setBusy(false); }
  };

  const toggleBtn = (type, label) => (
    <button
      onClick={() => setTargetType(type)}
      style={{
        padding: '5px 12px', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
        border: `1px solid ${targetType === type ? B.navy : B.border}`,
        background: targetType === type ? B.tealLt : B.white,
        color: targetType === type ? B.text : B.textMd,
        borderRadius: 6, fontWeight: targetType === type ? 600 : 400,
      }}
    >{label}</button>
  );

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: B.text, marginBottom: 2 }}>
          {isThread && <span style={{ fontSize: 11, fontWeight: 700, color: B.navy, background: B.tealLt, border: `1px solid ${B.navy}`, borderRadius: 10, padding: '1px 8px', marginRight: 8 }}>📧 THREAD · {items.length}</span>}
          {subject}
        </div>
        <div style={{ fontSize: 12, color: B.textMd }}>
          {participants.length > 0
            ? <>Participants: <strong>{participants.slice(0, 4).join(', ')}</strong>{participants.length > 4 ? ` +${participants.length - 4}` : ''}</>
            : <>From <strong>{newest.fromName || newest.fromEmail || 'Unknown'}</strong></>}
          {newest.receivedUTC ? ` · latest ${fmt.date(newest.receivedUTC)}` : ''}
          {group.attachmentCount ? ` · ${group.attachmentCount} attachment${group.attachmentCount === 1 ? '' : 's'}` : ''}
        </div>
      </div>

      {/* Newest message summary; older messages available behind a toggle. */}
      {(showAll ? items.slice().reverse() : [newest]).map((m, i) => (
        <div key={m.inboxID}>
          {isThread && (
            <div style={{ fontSize: 11, color: B.textMd, marginTop: i === 0 ? 10 : 12, fontWeight: 600 }}>
              {m.fromName || m.fromEmail}{m.receivedUTC ? ` · ${fmt.date(m.receivedUTC)}` : ''}
            </div>
          )}
          <div
            style={{
              marginTop: isThread ? 4 : 10, padding: '10px 14px', borderRadius: 8,
              background: B.surfaceMuted, border: `1px solid ${B.border}`,
              fontSize: 13, color: B.text, lineHeight: 1.5, maxHeight: 260, overflowY: 'auto',
            }}
            dangerouslySetInnerHTML={{ __html: m.summaryHtml || '<em>No summary</em>' }}
          />
        </div>
      ))}
      {isThread && (
        <button onClick={() => setShowAll(v => !v)}
          style={{ marginTop: 8, background: 'none', border: 'none', color: B.navy, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', padding: 0 }}>
          {showAll ? '▴ Show latest only' : `▾ Show all ${items.length} messages`}
        </button>
      )}

      {suggestions.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: B.textMd, letterSpacing: 0.5, marginBottom: 6 }}>
            SUGGESTED DEALS — attach the {isThread ? 'thread' : 'email'} in one click
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {suggestions.map((s, i) => (
              <button
                key={`${s.dealId}:${s.dealDetailId || ''}:${i}`}
                onClick={() => assignTo({ dealId: s.dealId, dealDetailId: s.dealDetailId })}
                disabled={busy}
                title={`Attach to ${s.name}${s.dealDetailId ? ` (deal: ${s.dealName})` : ''}`}
                style={{
                  display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start',
                  gap: 1, padding: '6px 12px', borderRadius: 16, cursor: busy ? 'default' : 'pointer',
                  border: `1px solid ${B.navy}`, background: B.tealLt, color: B.text,
                  fontSize: 12, fontFamily: 'inherit', textAlign: 'left', opacity: busy ? 0.6 : 1,
                }}
              >
                <span style={{ fontWeight: 600 }}>+ {s.name}</span>
                <span style={{ fontSize: 10, color: B.textMd }}>
                  {s.dealDetailId
                    ? `${ENTITY_LABEL[s.entityType] || 'Detail'} · deal: ${s.dealName}`
                    : (ENTITY_LABEL[s.entityType] || 'Deal')}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
        {toggleBtn('deal', 'Deal / detail')}
        {toggleBtn('task', 'Task')}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 280, flex: 1 }}>
          {targetType === 'task' ? (
            <InlineSearchPicker
              label={`Attach ${isThread ? 'thread' : 'email'} to task`}
              value={taskCombo}
              onChange={setTaskCombo}
              items={taskItems}
              placeholder="Search tasks…"
              clearLabel="— No task —"
            />
          ) : (
            <InlineSearchPicker
              label={suggestions.length ? 'Or find another deal / deal detail' : `Attach ${isThread ? 'thread' : 'email'} to deal / deal detail`}
              value={dealCombo}
              onChange={setDealCombo}
              items={dealItems}
              placeholder="Search deals & details…"
              clearLabel="— No deal —"
            />
          )}
        </div>
        <Btn onClick={assign} disabled={busy} size="sm">{busy ? 'Working…' : (isThread ? 'Attach thread' : 'Attach as note')}</Btn>
        <Btn onClick={dismiss} disabled={busy} variant="ghost" size="sm">Dismiss</Btn>
      </div>
    </Card>
  );
};

// Renders the /api/email-inbox/diagnostics report: actionable hints up top, a
// compact status grid, and the raw JSON for support. Admin-only (the endpoint
// enforces the role).
const DiagnosticsPanel = ({ report, onClose }) => {
  const yn = (v) => v === true ? <Icon name="check" size={14} color={B.green} style={{ display: 'inline-block', verticalAlign: 'middle' }} /> : v === false ? <Icon name="close" size={14} color={B.red} style={{ display: 'inline-block', verticalAlign: 'middle' }} /> : '—';
  const rows = [
    ['Capture mailbox',        report.env?.captureMailbox || '(not set)'],
    ['Webhook base URL',       report.env?.webhookBaseUrl ? `${report.env.webhookBaseUrl} (${report.env.webhookBaseUrlSource})` : '(not set)'],
    ['Webhook secret set',     yn(report.env?.webhookSecretSet)],
    ['Graph credentials set',  yn(report.env?.graphConfigured)],
    ['Tables exist',           yn(report.schema?.tablesExist)],
    ['Captures stored (total)', report.schema?.totalCount ?? '—'],
    ['Live Graph subscription', yn(report.subscription?.liveFound)],
    ['Can read mailbox',       yn(report.mailboxAccess?.ok) + (report.mailboxAccess?.recentMessages != null ? ` (${report.mailboxAccess.recentMessages} recent)` : '')],
    ['Tracked threads (active)', report.threads?.activeCount ?? '—'],
    ['Threads pull-blocked',   report.threads?.pollUnavailableCount ?? '—'],
  ];
  return (
    <Card style={{ marginBottom: 16, border: `1px solid ${B.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: B.text }}>Capture pipeline diagnostics</div>
        <Btn variant="ghost" size="sm" onClick={onClose}><Icon name="close" size={13} /> Close</Btn>
      </div>

      {(report.hints || []).length > 0 && (
        <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 13, color: B.text, lineHeight: 1.5 }}>
          {report.hints.map((h, i) => <li key={i}>{h}</li>)}
        </ul>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 14px', fontSize: 12, color: B.textMd }}>
        {rows.map(([k, v]) => (
          <Fragment key={k}>
            <div style={{ fontWeight: 600 }}>{k}</div>
            <div style={{ wordBreak: 'break-all' }}>{String(v)}</div>
          </Fragment>
        ))}
      </div>

      <details style={{ marginTop: 10 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, color: B.textMd }}>Raw report</summary>
        <pre style={{ fontSize: 11, background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 6, padding: 10, overflowX: 'auto', marginTop: 6 }}>
          {JSON.stringify(report, null, 2)}
        </pre>
      </details>
    </Card>
  );
};

export const EmailInbox = () => {
  const { data: items, loading, error, reload } = useApi(() => api.get('email-inbox'), []);
  const { dealPickerItems } = useDealPickerItems();
  const { taskPickerItems } = useTaskPickerItems();
  const { toast } = useToast();
  const allThreads = groupThreads(items);
  // "My emails" = threads holding a capture this person sent (server-computed
  // SentByMe); the whole thread is shown, since attaching it files them all.
  const myThreads = allThreads.filter(g => g.items.some(m => m.sentByMe));
  const [tab, setTab] = useState('mine'); // 'mine' | 'all'
  const threads = tab === 'mine' ? myThreads : allThreads;
  const [diag, setDiag]         = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);

  const runDiagnostics = async () => {
    setDiagBusy(true);
    try {
      const r = await api.get('email-inbox/diagnostics');
      setDiag(r);
    } catch (e) { toast.error(e.message || 'Diagnostics failed'); }
    finally { setDiagBusy(false); }
  };

  const scanMailbox = async () => {
    setScanBusy(true);
    try {
      const r = await api.post('email-inbox/scan', {});
      if (r.errors && r.errors.length) {
        toast.error(`Scanned ${r.scanned}: ${r.captured} captured, ${r.errors.length} failed — ${r.errors[0].error}`);
      } else {
        toast.success(`Scanned ${r.scanned}: ${r.captured} captured, ${r.duplicates} already had`);
      }
      reload();
    } catch (e) { toast.error(e.message || 'Scan failed'); }
    finally { setScanBusy(false); }
  };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <SectionHeader
        title="Email Inbox"
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" size="sm" onClick={scanMailbox} disabled={scanBusy}>
              <Icon name="download" size={14} /> {scanBusy ? 'Scanning…' : 'Scan mailbox'}
            </Btn>
            <Btn variant="ghost" size="sm" onClick={runDiagnostics} disabled={diagBusy}>
              <Icon name="diagnostics" size={14} /> {diagBusy ? 'Checking…' : 'Diagnostics'}
            </Btn>
            <Btn variant="ghost" size="sm" onClick={reload}><Icon name="refresh" size={14} /> Refresh</Btn>
          </div>
        }
      />
      <p style={{ fontSize: 12, color: B.textMd, marginTop: -6, marginBottom: 16 }}>
        Emails forwarded to — or CC'd on — the Harvus capture mailbox, grouped by thread. Attach a
        thread to a deal, deal detail, or task and it becomes one rolling note (Claude summary +
        each original email as a PDF). Later replies then <strong>track automatically</strong> — pulled
        from participants' mailboxes — as long as a Harvus person stays on the conversation.
      </p>

      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${B.border}`, marginBottom: 16 }}>
        {[['mine', 'My emails', myThreads.length], ['all', 'All emails', allThreads.length]].map(([key, label, n]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{
              padding: '8px 14px', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
              background: 'none', border: 'none', marginBottom: -1,
              borderBottom: `2px solid ${tab === key ? B.navy : 'transparent'}`,
              color: tab === key ? B.text : B.textMd, fontWeight: tab === key ? 600 : 400,
            }}
          >{label}{!loading && !error ? ` (${n})` : ''}</button>
        ))}
      </div>

      {diag && <DiagnosticsPanel report={diag} onClose={() => setDiag(null)} />}

      {loading && <Spinner label="Loading captured emails…" />}
      {error && <ErrorMsg msg={error} onRetry={reload} />}

      {!loading && !error && threads.length === 0 && (
        <Card>
          <div style={{ textAlign: 'center', color: B.textMd, fontSize: 13, padding: '20px 0' }}>
            {tab === 'mine' && allThreads.length > 0
              ? <>None of the {allThreads.length} waiting {allThreads.length === 1 ? 'thread was' : 'threads were'} sent by you. See <strong>All emails</strong> for everyone else's.</>
              : 'No captured emails waiting. Forward or CC an email to the capture mailbox to see it here.'}
          </div>
        </Card>
      )}

      {!loading && !error && threads.map(group => (
        <ThreadCard
          key={group.key}
          group={group}
          dealItems={dealPickerItems}
          taskItems={taskPickerItems}
          onAssigned={reload}
        />
      ))}
    </div>
  );
};
